import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import {
  Container, Card, CardContent, Typography, TextField,
  Button, Grid, Alert, CircularProgress, Box, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, Chip
} from '@mui/material';
import { PersonAdd, Delete, Refresh } from '@mui/icons-material';
import { useAuth } from '../App';
import axios from 'axios';

const API_BASE = API_BASE_URL;

const AdminUserManagementPage = () => {
  const { user } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('Admin');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Load the user list automatically when the page opens
  useEffect(() => {
    if (user?.idToken) {
      loadUsers();
    }
  }, [user?.idToken]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await axios.post(`${API_BASE}/admin/users/create`, {
        email,
        password,
        role,
        firstName,
        lastName
      }, {
        headers: {
          'Authorization': `Bearer ${user.idToken}`
        }
      });

      setSuccess(res.data?.message || `${role} account created successfully for ${email}`);
      
      // Clear form
      setEmail('');
      setPassword('');
      setFirstName('');
      setLastName('');
      setRole('Admin');
      
      // Reload users list
      loadUsers();
      
    } catch (err) {
      console.error('Error creating user:', err);
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API_BASE}/admin/users/list`, {
        headers: {
          'Authorization': `Bearer ${user.idToken}`
        }
      });

      setUsers(response.data.users || []);
      
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleDeleteUser = async (uid, email) => {
    if (!window.confirm(`Are you sure you want to delete user: ${email}?`)) {
      return;
    }

    try {
      await axios.delete(`${API_BASE}/admin/users/${uid}`, {
        headers: {
          'Authorization': `Bearer ${user.idToken}`
        }
      });

      setSuccess(`User ${email} deleted successfully`);
      loadUsers();
      
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(password);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#046A38' }}>
        👥 User Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Create User Form */}
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                Create New User
              </Typography>

              <form onSubmit={handleCreateUser}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        label="Password"
                        type="text"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        helperText="Minimum 6 characters"
                      />
                      <Button
                        variant="outlined"
                        onClick={generatePassword}
                        sx={{ minWidth: '120px' }}
                      >
                        Generate
                      </Button>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      select
                      label="Role"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      required
                    >
                      <MenuItem value="Admin">Admin</MenuItem>
                      <MenuItem value="View Only">View Only</MenuItem>
                      <MenuItem value="Alumni">Alumni (for testing)</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={loading ? <CircularProgress size={20} /> : <PersonAdd />}
                      disabled={loading}
                      sx={{
                        bgcolor: '#046A38',
                        '&:hover': { bgcolor: '#035028' },
                        py: 1.5
                      }}
                    >
                      {loading ? 'Creating...' : 'Create User'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {/* Instructions */}
        <Grid item xs={12} md={6}>
          <Card elevation={3} sx={{ bgcolor: '#f9f9f9' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                📋 Instructions
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>Admin:</strong> Full access to all features including user management, approvals, and database editing.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>View Only:</strong> Can view the alumni database but cannot make changes.
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>Alumni:</strong> Can view their profile, generate ID cards, and see statistics.
              </Typography>

              <Box sx={{ mt: 3, p: 2, bgcolor: '#fff3cd', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  ⚠️ Important:
                </Typography>
                <Typography variant="caption">
                  • Accounts are created activated (no verification needed)<br />
                  • Login details are emailed to the new user automatically<br />
                  • Encourage users to change their password after first login
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* User List */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Existing Users
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={loadUsers}
                  disabled={loadingUsers}
                >
                  {loadingUsers ? 'Loading...' : 'Refresh'}
                </Button>
              </Box>

              {users.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell><strong>Email</strong></TableCell>
                        <TableCell><strong>Name</strong></TableCell>
                        <TableCell><strong>Role</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell align="right"><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((u) => (
                        <TableRow key={u.uid}>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>{u.displayName || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip
                              label={u.role}
                              size="small"
                              color={u.role === 'Admin' ? 'error' : u.role === 'View Only' ? 'warning' : 'success'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={u.disabled ? 'Disabled' : 'Active'}
                              size="small"
                              color={u.disabled ? 'default' : 'success'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteUser(u.uid, u.email)}
                              disabled={u.email === user.email}
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : loadingUsers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No users found. Create one using the form above.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AdminUserManagementPage;
